import { DateTime } from "luxon";
import Filters from "byzantine/src/filters";

function getTimeToNowString(time: string) {
  const date = DateTime.fromISO(time);
  const timeDiff = date.diffNow(["years", "months", "days", "hours", "minutes"]);
  const timeDiffObject = timeDiff.toObject();
  if (timeDiffObject.years) {
    return `${Math.ceil(timeDiffObject.years * -1)}y`;
  }
  if (timeDiffObject.months) {
    return `${Math.ceil(timeDiffObject.months * -1)}mo`;
  }
  if (timeDiffObject.days) {
    return `${Math.ceil(timeDiffObject.days * -1)}d`;
  }
  if (timeDiffObject.hours) {
    return `${Math.ceil(timeDiffObject.hours * -1)}h`;
  }
  if (timeDiffObject.minutes) {
    return `${Math.ceil(timeDiffObject.minutes * -1)}min`;
  }

  return "";
}

function getContextualTimestamp(time: string) {
  // just show time if timestamp is from today,
  // show month + day + time if timestamp is from this year,
  // show full datetime if timestamp is from a previous year

  const date = DateTime.fromISO(time).setZone("local");
  const isToday = date.hasSame(DateTime.now(), "day");
  const isThisYear = date.hasSame(DateTime.now(), "year");

  if (isToday) {
    return Filters.time(time);
  } else if (isThisYear) {
    return Filters.shortMonthDayTime(time);
  }

  return Filters.shortMonthDayYearTime(time);
}

/**
 * This function returns the current time zone abbreviation for a given IANA time zone.
 * Example input: "America/New_York"
 * Example output: "EDT"
 * @param timeZone The IANA time zone identifier to retrieve information for.
 * @returns The current abbreviation of the time zone.
 */
function getTimeZoneAbbreviation(timeZone: string) {
  const parts = Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "short",
  }).formatToParts(new Date());

  const timeZonePart = parts.find((part) => part.type === "timeZoneName");
  return timeZonePart?.value || "";
}

/**
 * Formats a timestamp in the given time zone, followed by that time zone's abbreviation.
 * Example input: "2026-09-15T14:51:00.000Z", "America/New_York"
 * Example output: "Sep 15, 2026, 10:51 am EDT"
 * @param timestamp An ISO 8601 timestamp.
 * @param timeZone The IANA time zone identifier to display the timestamp in.
 * @returns The formatted timestamp, or the timestamp unchanged if it can't be parsed.
 */
function formatTimestampInTimeZone(timestamp: string, timeZone: string) {
  const date = DateTime.fromISO(timestamp, { zone: timeZone, locale: "en-US" });
  if (!date.isValid) {
    return timestamp;
  }

  return `${Filters.shortMonthDayYearTime(date.toISO())} ${date.offsetNameShort}`;
}

/**
 * Converts a date and time into a datetime string in the specified time zone.
 * Returns null if either date or time is empty.
 * @param date The date in ISO format (YYYY-MM-DD).
 * @param time The time in ISO format (HH:mm).
 * @param timeZone The IANA time zone identifier (e.g., "America/New_York").
 * @returns A datetime string in ISO format or null if inputs are invalid.
 */
function convertDateAndTimeToDatetime(
  date: string,
  time: string,
  timeZone?: string
): string | null {
  return date !== "" && time !== ""
    ? DateTime.fromISO(`${date}T${time}`).setZone(timeZone).toISO()
    : null;
}

export {
  getTimeToNowString,
  getContextualTimestamp,
  getTimeZoneAbbreviation,
  formatTimestampInTimeZone,
  convertDateAndTimeToDatetime,
};
